import { useEffect, useMemo, useState } from "react"

const emptyMap = new Map()

let cachedFilters = null
let cachedFiltersPromise = null
let cachedFiltersError = null

const toNormalizedCode = (value) => {
  if (value === null || value === undefined) {
    return ""
  }

  return String(value).trim().toLowerCase()
}

const toCodeSet = (values) => {
  return new Set(
    (values || [])
      .map((value) => toNormalizedCode(value))
      .filter(Boolean)
  )
}

const hasOverlap = (a, b) => {
  for (const value of a) {
    if (b.has(value)) {
      return true
    }
  }

  return false
}

const getAllowedSchoolCodes = ({ profile, schoolMemberships, subdomainSchoolId }) => {
  const membershipCodes = Array.isArray(schoolMemberships)
    ? schoolMemberships.flatMap((membership) => [membership?.sch_code, membership?.id])
    : []

  return toCodeSet([
    ...(profile?.schCodes || []),
    ...membershipCodes,
    subdomainSchoolId,
  ])
}

const canAccessCurriculumItem = (item, allowedSchoolCodes) => {
  if (!item) {
    return false
  }

  if (item.is_public) {
    return true
  }

  const itemSchoolCodes = toCodeSet(Array.from(item.sch_codes_with_access || []))
  if (!itemSchoolCodes.size || !allowedSchoolCodes.size) {
    return false
  }

  return hasOverlap(itemSchoolCodes, allowedSchoolCodes)
}

const loadCurriculumFilters = async (firebase) => {
  if (!firebase) {
    return {
      moduleMap: emptyMap,
      programMap: emptyMap,
    }
  }

  if (cachedFilters) {
    return cachedFilters
  }

  if (cachedFiltersPromise) {
    return cachedFiltersPromise
  }

  cachedFiltersPromise = Promise.all([
    firebase.getCurriculumAnalyticsModuleFilters(),
    firebase.getCurriculumAnalyticsProgramFilters(),
  ])
    .then(([moduleMap, programMap]) => {
      cachedFilters = {
        moduleMap: moduleMap instanceof Map ? moduleMap : emptyMap,
        programMap: programMap instanceof Map ? programMap : emptyMap,
      }
      cachedFiltersError = null
      return cachedFilters
    })
    .catch((error) => {
      cachedFiltersError = error
      throw error
    })
    .finally(() => {
      cachedFiltersPromise = null
    })

  return cachedFiltersPromise
}

const toSearchTitle = (title) => {
  const baseTitle = String(title || "").trim()
  if (!baseTitle) {
    return ""
  }

  return baseTitle
}

export const useWorkReadinessSearchItems = ({
  firebase,
  profile,
  schoolMemberships,
  subdomainSchoolId,
}) => {
  const [filtersState, setFiltersState] = useState({
    loading: false,
    moduleMap: emptyMap,
    programMap: emptyMap,
    error: null,
  })

  useEffect(() => {
    let isMounted = true

    if (!firebase) {
      setFiltersState((prev) => ({
        ...prev,
        loading: false,
        moduleMap: emptyMap,
        programMap: emptyMap,
      }))
      return () => {
        isMounted = false
      }
    }

    if (cachedFilters) {
      setFiltersState({
        loading: false,
        moduleMap: cachedFilters.moduleMap,
        programMap: cachedFilters.programMap,
        error: cachedFiltersError,
      })
      return () => {
        isMounted = false
      }
    }

    setFiltersState((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }))

    loadCurriculumFilters(firebase)
      .then((filters) => {
        if (!isMounted) {
          return
        }

        setFiltersState({
          loading: false,
          moduleMap: filters.moduleMap,
          programMap: filters.programMap,
          error: null,
        })
      })
      .catch((error) => {
        if (!isMounted) {
          return
        }

        setFiltersState({
          loading: false,
          moduleMap: emptyMap,
          programMap: emptyMap,
          error,
        })
      })

    return () => {
      isMounted = false
    }
  }, [firebase])

  const allowedSchoolCodes = useMemo(() => {
    return getAllowedSchoolCodes({ profile, schoolMemberships, subdomainSchoolId })
  }, [profile, schoolMemberships, subdomainSchoolId])

  const items = useMemo(() => {
    const moduleEntries = Array.from(filtersState.moduleMap.values())
      .filter((moduleItem) => canAccessCurriculumItem(moduleItem, allowedSchoolCodes))
      .map((moduleItem) => ({
        id: `module-${moduleItem.id}`,
        title: toSearchTitle(moduleItem.title),
        path: `/modules/${moduleItem.id}`,
        work_readiness_type_text: "module",
        work_readiness_aliases_text: "module modules assignment assignments curriculum work readiness skills development",
      }))

    const programEntries = Array.from(filtersState.programMap.values())
      .filter((programItem) => canAccessCurriculumItem(programItem, allowedSchoolCodes))
      .map((programItem) => ({
        id: `program-${programItem.id}`,
        title: toSearchTitle(programItem.title),
        path: `/programs/${programItem.id}`,
        work_readiness_type_text: "program",
        work_readiness_aliases_text: "program programs pathway pathways curriculum work readiness skills development",
      }))

    return [...moduleEntries, ...programEntries].sort((a, b) => a.title.localeCompare(b.title))
  }, [filtersState.moduleMap, filtersState.programMap, allowedSchoolCodes])

  return {
    items,
    loading: filtersState.loading,
    error: filtersState.error,
  }
}

export default useWorkReadinessSearchItems
